<template>
  <div>
    <div class="card card-container">
      <div class="tw-py-10">
        <div class="tw-flex tw-items-center tw-justify-between tw-flex-wrap">
          <div>
            <h2 class="page-title">Billing</h2>
          </div>
          <div class="tw-flex tw-flex-wrap">
            <div>
              <SpinnerButton
                v-if="hasStripeId && !isManuallyBilled"
                :loading="loading || loadingPortal"
                :spinner-only="true"
                type="button"
                data-cy="billing-portal"
                :disabled="!hasStripeId"
                @click="redirectToBillingPortal"
              >
                Open billing portal
                <SvgIcon name="link" class="tw-ml-1 tw-w-6 tw-h-6" />
              </SpinnerButton>
            </div>
          </div>
        </div>
        <div v-if="loading" class="vld-parent" style="min-height: 10rem;">
          <Loading loader="dots" :is-full-page="false" :active="loading" />
        </div>
        <p v-if="isManuallyBilled && !loading" class="tw-mt-1 w-text-gray-700">
          Contact our support team to manage your subscription, billing and
          payment details, including invoice history.
        </p>
        <p
          v-if="subscription && !isManuallyBilled && !loading"
          class="tw-mt-1 w-text-gray-700"
        >
          Use the billing portal to manage subscription, billing and payment
          details, including invoice history.
        </p>
        <div v-if="hasPaymentIssue && !loading">
          <p class="tw-mt-1 w-text-gray-700">
            <strong
              >You currently have a payment issue with your
              subscription.</strong
            >
          </p>
          <p class="tw-mt-2 w-text-gray-700">
            Please use the billing portal to update your billing details and
            avoid disruption to your account.
          </p>
        </div>
        <div
          v-if="(!loading && !isSubscribed) || (isCanceled && !isInGracePeriod)"
          class="tw-mt-1 w-text-gray-700"
        >
          <p v-if="isOnFreePlan || isOnFreeTrial">
            You are currently on the
            <strong>{{ activeCompany.billing_plan.displayName }}</strong> plan
            <strong>{{ used }}</strong> of
            <strong>{{ planLimit === null ? 'unlimited' : planLimit }}</strong>
            employees.
          </p>
          <div v-if="isOnFreePlan">
            <p class="tw-mt-6">
              Eager to share the joy of Leave Dates with your entire team?
            </p>
            <p class="tw-mt-6">
              Our premium plan lets you do just that. All the same great
              features but aimed at teams with more than five employees.
            </p>
          </div>
        </div>
        <div v-if="!loading && (isCanceled || isPreviousCustomer)">
          <div v-if="isInGracePeriod">
            <p class="tw-mt-6">
              Your subscription has been <strong>cancelled</strong>.
            </p>
            <p class="tw-mt-6">
              To access your previous invoices or to renew your subscription use
              the billing portal link above.
            </p>
          </div>
          <div v-else>
            <p class="tw-mt-6">
              Your previous subscription has been <strong>cancelled</strong>.
            </p>
            <p class="tw-mt-6">
              To access your previous invoices use the billing portal link above
              or upgrade using the button below.
            </p>
            <SpinnerButton
              :loading="loadingRenew"
              :spinner-only="true"
              type="button"
              data-cy="billing-renew"
              class="tw-mt-8"
              @click="renewSubscriptionCheckout"
            >
              Upgrade
            </SpinnerButton>
          </div>
        </div>
        <div
          v-if="
            !loading &&
              (subscription ||
                (isCanceled && isInGracePeriod) ||
                hasPaymentIssue)
          "
          class="card tw-mt-10 tw-max-w-5xl"
          data-cy="billing-area"
        >
          <h3 class="sub-header tw-pb-5">Subscription</h3>
          <div>
            <div v-if="!isManuallyBilled">
              <TextWithIcon icon="rocket-f">
                Plan:
                <strong>{{ activeCompany.billing_plan.displayName }}</strong>
              </TextWithIcon>
              <TextWithIcon icon="clock-outline">
                Frequency: <strong>{{ frequency }}</strong>
              </TextWithIcon>
              <TextWithIcon icon="user-group">
                Employee limit: <strong>{{ stripeSeatCount }}</strong>
              </TextWithIcon>

              <div v-if="isCanceled && isInGracePeriod">
                <TextWithIcon icon="calendar">
                  Subscription end date:
                  <strong> {{ currentPeriodEndsAt }}</strong>
                </TextWithIcon>
              </div>
              <div v-else>
                <TextWithIcon icon="calendar">
                  Next billing date:
                  <strong> {{ invoiceDate }}</strong>
                </TextWithIcon>

                <TextWithIcon icon="credit-card">
                  Amount: <strong>{{ billingAmount }}</strong>
                  <span v-if="hasTax"> (excluding VAT)</span>
                </TextWithIcon>
              </div>
            </div>

            <div class="card tw-bg-gray-100 tw-mt-10">
              <h3 class="sub-header tw-pb-5">Usage</h3>

              <p class="tw-pb-2">{{ used }}/{{ stripeSeatCount }} employees</p>

              <BillingUsageChart :used="used" :limit="stripeSeatCount" />

              <p class="tw-pt-10">
                You have {{ planStatusText }}
                {{ frequency.toLocaleLowerCase() }} subscription for
                {{ stripeSeatCount }} employees.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loading && appsumo" class="card tw-max-w-5xl tw-mb-8">
        <h3 class="sub-header tw-pb-5">AppSumo license</h3>

        <TextWithIcon v-if="!subscription" icon="rocket-f">
          Plan:
          <strong>{{ activeCompany.billing_plan.displayName }}</strong>
        </TextWithIcon>
        <TextWithIcon icon="user-group">
          Employee limit: <strong>{{ appsumoSeatCount }}</strong>
        </TextWithIcon>

        <p class="tw-mt-6 tw-mb-4 w-text-gray-700">
          Manage your AppSumo license directly on AppSumo.
        </p>
        <div class="tw-flex tw-flex-wrap tw-gap-3">
          <a
            v-if="appsumo.license_redemption_url"
            :href="appsumo.license_redemption_url"
            target="_blank"
            rel="noopener"
          >
            <SpinnerButton type="button" data-cy="appsumo-redeem">
              Redeem license
              <SvgIcon name="link" class="tw-ml-1 tw-w-6 tw-h-6" />
            </SpinnerButton>
          </a>
          <a
            v-if="appsumo.license_change_plan_url"
            :href="appsumo.license_change_plan_url"
            target="_blank"
            rel="noopener"
          >
            <SpinnerButton type="button" data-cy="appsumo-change-plan">
              Change AppSumo plan
              <SvgIcon name="link" class="tw-ml-1 tw-w-6 tw-h-6" />
            </SpinnerButton>
          </a>
        </div>
        <p
          v-if="
            !appsumo.license_redemption_url && !appsumo.license_change_plan_url
          "
          class="tw-mt-2 w-text-gray-700"
        >
          AppSumo links are temporarily unavailable. Please refresh to try
          again.
        </p>

        <div v-if="appsumo.discount" class="card tw-bg-gray-100 tw-mt-6">
          <h4 class="sub-header tw-pb-2">
            {{ appsumo.discount.percent }}% upgrade credit available
          </h4>
          <p class="tw-pb-2">
            Upgrade to a paid plan before
            <strong>{{ discountEndsAt }}</strong> to claim credit:
          </p>
          <ul class="tw-list-disc tw-ml-5">
            <li
              v-for="credit in appsumo.discount.credits"
              :key="credit.currency"
            >
              {{ formatMoney(credit) }} {{ credit.currency }}
            </li>
          </ul>
        </div>

        <div class="card tw-bg-gray-100 tw-mt-6">
          <h3 class="sub-header tw-pb-5">Usage</h3>
          <p class="tw-pb-2">{{ used }}/{{ appsumoSeatCount }} employees</p>
          <BillingUsageChart :used="used" :limit="appsumoSeatCount" />
          <p class="tw-pt-10">
            You have an active AppSumo license for
            {{ appsumoSeatCount }} employees.
          </p>
        </div>
      </div>

      <div v-if="!loading && canShowCheckout && planPrices.length">
        <h3 class="sub-header tw-pb-5">Create a Stripe Subscription</h3>
        <div class="tw-flex tw-flex-wrap tw-gap-3 tw-max-w-3xl">
          <div class="tw-flex-1 tw-min-w-[16rem]">
            <v-select
              v-model="selectedCheckoutPlan"
              :options="planPrices"
              :multiple="false"
              :searchable="false"
              :show-labels="false"
              :allow-empty="false"
              track-by="stripePriceId"
              label="label"
              placeholder="Select a plan"
              data-cy="checkout-plan-select"
            />
          </div>
          <div class="tw-w-40">
            <v-select
              v-model="selectedCurrency"
              :options="currencies"
              :multiple="false"
              :searchable="false"
              :show-labels="false"
              :allow-empty="false"
              placeholder="Currency"
              data-cy="checkout-currency-select"
            />
          </div>
        </div>
        <SpinnerButton
          :loading="loadingCheckout"
          :spinner-only="true"
          :disabled="!selectedCheckoutPlan"
          type="button"
          data-cy="checkout-session"
          class="tw-mt-4"
          @click="createCheckoutSession"
        >
          Checkout
        </SpinnerButton>
      </div>
    </div>
  </div>
</template>

<script>
import ValidatesForm from '@/mixins/ValidatesForm'
import Subscription from '@/mixins/Subscription'
import SpinnerButton from '@/components/SpinnerButton'
import TextWithIcon from '@/components/billing/TextWithIcon'
import BillingUsageChart from '@/components/billing/BillingUsageChart'
import Billing from '@/api/billing/Billing'
import { CREATE_CHECKOUT_SESSION } from '@/graphql/mutations/checkout-session'
import Loading from 'vue-loading-overlay'
import vSelect from 'vue-multiselect'
import FormatDate from '@/mixins/FormatDate'

export default {
  name: 'Billing',

  components: {
    Loading,
    SpinnerButton,
    TextWithIcon,
    BillingUsageChart,
    vSelect,
  },

  mixins: [ValidatesForm, Subscription, FormatDate],

  data: () => ({
    loading: false,
    loadingRenew: false,
    loadingPortal: false,
    planLimit: 25,
    hasTax: true,
    hasStripeId: false,
    used: 8,
    frequency: 'Monthly',
    nextBillingDate: '2023-11-01',
    billingAmount: '$944.00',
    planType: null,
    planStatus: null,
    loadingCheckout: false,
    planPrices: [],
    selectedCheckoutPlan: null,
    currencies: ['GBP', 'EUR', 'USD'],
    selectedCurrency: 'GBP',
    appsumo: null,
  }),

  computed: {
    canShowCheckout() {
      return (
        !this.subscription &&
        !this.isCanceled &&
        !this.isPreviousCustomer &&
        !this.hasPaymentIssue &&
        !this.isManuallyBilled
      )
    },

    discountEndsAt() {
      if (!this.appsumo?.discount) return null
      return this.formatDateFromIsoToDayReadableDayNumberShortMonthYear(
        this.appsumo.discount.discount_ends_at
      )
    },

    stripeSeatCount() {
      return this.subscription?.quantity ?? 0
    },

    appsumoSeatCount() {
      return this.activeCompany.appsumo_license?.seats ?? 0
    },

    planStatusText() {
      if (this.isCanceled) return 'a cancelled'
      else if (this.hasPaymentIssue) return 'an overdue'
      else return 'an active'
    },

    invoiceDate() {
      return this.formatDateFromIsoToDayReadableDayNumberShortMonthYear(
        this.nextBillingDate
      )
    },

    isOnFreePlan() {
      return !this.isSubscriptionActive && this.planType === 'free_forever'
    },

    isOnFreeTrial() {
      return !this.isSubscriptionActive && this.planStatus === 'trial'
    },

    currentPeriodEndsAt() {
      if (!this.isSubscribed || (this.selectedPlan && !this.selectedPlan.name))
        return null

      return this.formatDateFromIsoToDayReadableDayNumberShortMonthYear(
        this.endsAt,
        'utc'
      )
    },
  },

  async mounted() {
    await Promise.all([this.getBillingDetails(), this.fetchPlanPrices()])
  },

  methods: {
    formatMoney(amount) {
      if (!amount || amount.cents == null) return null

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: amount.currency,
      }).format(amount.cents / 100)
    },

    async redirectToBillingPortal() {
      this.loadingPortal = true
      const { data } = await Billing.getBillingPortal(this.$route.query)

      this.redirect(data.redirect_url)
      this.loadingPortal = false
    },

    async renewSubscriptionCheckout() {
      this.loadingRenew = true
      const { data } = await Billing.getRenewSubscription(this.$route.query)

      this.redirect(data.redirect_url)
      this.loadingRenew = false
    },

    redirect(url) {
      if (url) {
        window.open(url, '_self')
      }
    },

    async getBillingDetails() {
      this.loading = true
      try {
        const { data } = await Billing.getBillingDetails(this.$route.query)

        this.hasStripeId = data.stripe !== null
        this.hasTax = data.stripe?.upcoming?.has_tax ?? false
        this.planLimit = data.total_seat_count
        this.used = data.used_seat_count
        this.frequency =
          data.stripe?.interval === 'month' ? 'Monthly' : 'Annual'
        this.nextBillingDate = data.stripe?.upcoming?.billing_date ?? null
        this.billingAmount = this.formatMoney(data.stripe?.upcoming?.amount)
        this.planType = data.plan
        this.planStatus = data.status
        this.appsumo = data.appsumo
      } catch (e) {
        this.serverError()
      }
      this.loading = false
    },

    async fetchPlanPrices() {
      const allowedPlans = ['starter', 'essential', 'plus']
      try {
        const { data } = await Billing.getBillingPlans()
        this.planPrices = data.plan_prices
          .filter(planPrice => allowedPlans.includes(planPrice.plan))
          .flatMap(planPrice =>
            planPrice.prices.map(price => ({
              label: `${planPrice.plan.charAt(0).toUpperCase() +
                planPrice.plan.slice(1)} (${
                price.interval === 'month' ? 'Monthly' : 'Annual'
              })`,
              plan: planPrice.plan,
              interval: price.interval,
              stripePriceId: price.stripe_price_id,
            }))
          )
      } catch (e) {
        this.planPrices = []
      }
    },

    async createCheckoutSession() {
      if (!this.selectedCheckoutPlan) return

      this.loadingCheckout = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CREATE_CHECKOUT_SESSION,
          variables: {
            input: {
              company: this.activeCompany.id,
              cancelUrl: window.location.href,
              successUrl: window.location.href,
              currency: this.selectedCurrency,
              stripePriceId: this.selectedCheckoutPlan.stripePriceId,
            },
          },
        })

        const redirectUrl = data.createCheckoutSession
        if (redirectUrl) {
          this.redirect(redirectUrl)
        }
      } catch (error) {
        const message =
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          'Failed to create checkout session'
        this.serverError(message)
      }
      this.loadingCheckout = false
    },
  },
}
</script>
